import { CreditCard, UtensilsCrossed, Clock, CheckCircle, Package, TrendingUp, BarChart3,Pizza, ChefHat, EggFried, Utensils } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { dashboardCounts } from "../../../../services/dashboardService";

const Dashboard = () => {
  const { data: countAnalytics, isLoading } = useQuery({
    queryKey: ["dashboard-count"],
    queryFn: async () => {
      const res = await dashboardCounts();
      if (res?.status !== 200) toast.warning(res?.message);
      return res?.data;
    },
  });

  const totalOrders = countAnalytics?.order?.orderStatusCount 
    ? Object.values(countAnalytics.order.orderStatusCount).reduce((sum, count) => sum + count, 0)
    : 0;

  const totalMenus = countAnalytics?.menu?.menuStatusCount
    ? Object.values(countAnalytics.menu.menuStatusCount).reduce((sum, count) => sum + count, 0)
    : 0;

  const totalCategories = countAnalytics?.category?.categoryStatusCount
    ? Object.values(countAnalytics.category.categoryStatusCount).reduce((sum, count) => sum + count, 0)
    : 0;

  const mainStats = [
    {
      title: "Total Orders",
      value: isLoading ? "..." : totalOrders.toString(),
      icon: <UtensilsCrossed className="w-10 h-10 text-blue-500" />,
      gradient: "from-blue-500 via-blue-400 to-blue-300",
      bgGradient: "from-blue-500/20 via-blue-400/10 to-transparent",
      shadowColor: "shadow-blue-500/20",
    },
    {
      title: "Total Tables",
      value: isLoading ? "..." : (countAnalytics?.table?.tableCount || 0).toString(),
      icon: <Utensils className="w-10 h-10 text-purple-500" />,
      gradient: "from-purple-500 via-purple-400 to-purple-300",
      bgGradient: "from-purple-500/20 via-purple-400/10 to-transparent",
      shadowColor: "shadow-purple-500/20",
    },
    {
      title: "Active Menus",
      value: isLoading ? "..." : (countAnalytics?.menu?.menuStatusCount?.active || 0).toString(),
      icon: <Pizza className="w-10 h-10 text-teal-500" />,
      gradient: "from-teal-500 via-teal-400 to-teal-300",
      bgGradient: "from-teal-500/20 via-teal-400/10 to-transparent",
      shadowColor: "shadow-teal-500/20",
    },
    {
      title: "Active Categories",
      value: isLoading ? "..." : (countAnalytics?.category?.categoryStatusCount?.active || 0).toString(),
      icon: <BarChart3 className="w-10 h-10 text-emerald-500" />,
      gradient: "from-emerald-500 via-emerald-400 to-emerald-300",
      bgGradient: "from-emerald-500/20 via-emerald-400/10 to-transparent",
      shadowColor: "shadow-emerald-500/20",
    },
  ];

  const orderStats = [
    {
      title: "Pending Orders",
      value: isLoading ? "..." : (countAnalytics?.order?.orderStatusCount?.pending || 0).toString(),
      icon: <Clock className="w-8 h-8 text-orange-500" />,
      color: "from-orange-500/15 to-orange-500/5",
      iconBg: "bg-gradient-to-br from-orange-100 to-orange-50",
      borderColor: "border-orange-200/50",
    },
    {
      title: "Partial Orders",
      value: isLoading ? "..." : (countAnalytics?.order?.orderStatusCount?.partial || 0).toString(),
      icon: <Package className="w-8 h-8 text-yellow-500" />,
      color: "from-yellow-500/15 to-yellow-500/5",
      iconBg: "bg-gradient-to-br from-yellow-100 to-yellow-50",
      borderColor: "border-yellow-200/50",
    },
    {
      title: "Paid Orders",
      value: isLoading ? "..." : (countAnalytics?.order?.orderStatusCount?.paid || 0).toString(),
      icon: <CheckCircle className="w-8 h-8 text-green-500" />,
      color: "from-green-500/15 to-green-500/5",
      iconBg: "bg-gradient-to-br from-green-100 to-green-50",
      borderColor: "border-green-200/50",
    },
  ];

  const tableStats = [
    {
      title: "Pending Tables",
      value: isLoading ? "..." : (countAnalytics?.table?.tableStatusCount?.pending || 0).toString(),
      icon: <Clock className="w-8 h-8 text-indigo-500" />,
      color: "from-indigo-500/15 to-indigo-500/5",
      iconBg: "bg-gradient-to-br from-indigo-100 to-indigo-50",
      borderColor: "border-indigo-200/50",
    },
    {
      title: "Paid Tables",
      value: isLoading ? "..." : (countAnalytics?.table?.tableStatusCount?.paid || 0).toString(),
      icon: <CreditCard className="w-8 h-8 text-pink-500" />,
      color: "from-pink-500/15 to-pink-500/5",
      iconBg: "bg-gradient-to-br from-pink-100 to-pink-50",
      borderColor: "border-pink-200/50",
    },
    {
      title: "Total Menu Items",
      value: isLoading ? "..." : totalMenus.toString(),
      icon: <Pizza className="w-8 h-8 text-cyan-500" />,
      color: "from-cyan-500/15 to-cyan-500/5",
      iconBg: "bg-gradient-to-br from-cyan-100 to-cyan-50",
      borderColor: "border-cyan-200/50",
    },
    {
      title: "Total Categories",
      value: isLoading ? "..." : totalCategories.toString(),
      icon: <BarChart3 className="w-8 h-8 text-violet-500" />,
      color: "from-violet-500/15 to-violet-500/5",
      iconBg: "bg-gradient-to-br from-violet-100 to-violet-50",
      borderColor: "border-violet-200/50",
    },
  ];

  return (
    <div className="min-h-screen md:py-12 md:px-6">
      <style>{`
        @keyframes gradientMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes titleGradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: titleGradient 5s ease infinite;
        }
        @keyframes utensilMove {
          0%, 100% { transform: rotate(0deg) scale(1); }
          50% { transform: rotate(-15deg) scale(1.15); }
        }
        .animate-utensil {
          animation: utensilMove 2.5s ease-in-out infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        .animate-shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          background-size: 1000px 100%;
          animation: shimmer 3s infinite;
        }
        .card-hover {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .card-hover:hover {
          transform: translateY(-8px) scale(1.02);
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        .pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>

      <div className="text-center mb-12 animate-float">
        <div className="flex items-center justify-center gap-4 mb-3">
          <UtensilsCrossed className="w-5 lg:w-12 h-12 text-[#224722] animate-utensil drop-shadow-lg" />
          <h1 className="text-3xl lg:text-6xl font-black bg-gradient-to-r from-[#175717] via-[#64b364] to-[#175717] bg-clip-text text-transparent animate-gradient drop-shadow-sm">
            Dashboard
          </h1>
          <UtensilsCrossed className="w-5 lg:w-12 h-12 text-[#224722] animate-utensil drop-shadow-lg" />
        </div>
        <p className="text-gray-500 text-lg font-medium">Real-time restaurant analytics and insights</p>
      </div>

      <div className="max-w-7xl 3xl:max-w-full mx-auto space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {mainStats.map((stat, index) => (
            <div
              key={index}
              className={`relative overflow-hidden bg-white rounded-3xl p-5 md:p-8 card-hover ${stat.shadowColor} shadow-xl border border-gray-100 group`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.bgGradient} opacity-60`}></div>
              <div className="absolute inset-0 animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10 flex w-full items-center lg:items-start gap-5 flex-row lg:flex-col">
                <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${stat.gradient} shadow-lg xls:mb-4 pulse-glow`}>
                  <div className="bg-white rounded-xl p-2">
                    {stat.icon}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider mb-2">
                    {stat.title}
                  </h3>
                  
                  <div className="flex items-end justify-between">
                    <p className="text-4xl font-black text-gray-900">
                      {stat.value}
                    </p>
                  </div>
                </div>
              </div>
              <div className={`absolute -right-8 -bottom-8 w-32 h-32 bg-gradient-to-br ${stat.gradient} rounded-full opacity-10 blur-2xl`}></div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-orange-400 rounded-xl shadow-lg">
              <UtensilsCrossed className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Order Status</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {orderStats.map((stat, index) => (
              <div
                key={index}
                className={`bg-gradient-to-br ${stat.color} border ${stat.borderColor} rounded-2xl p-6 card-hover`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-3 ${stat.iconBg} rounded-xl shadow-md`}>
                    {stat.icon}
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1">
                      {stat.title}
                    </h3>
                    <p className="text-3xl font-black text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-400 rounded-xl shadow-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Quick Stats</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tableStats.map((stat, index) => (
              <div
                key={index}
                className={`bg-gradient-to-br ${stat.color} border ${stat.borderColor} rounded-2xl p-6 card-hover`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-3 ${stat.iconBg} rounded-xl shadow-md`}>
                    {stat.icon}
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1">
                      {stat.title}
                    </h3>
                    <p className="text-3xl font-black text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;